/**
 * The edit mode's body editor (handoff §15.2): a serif textarea that grows with its
 * text, with every occurrence of a gap form kept under a dashed accent underline
 * while the learner types.
 *
 * The underline is drawn on a mirror behind the textarea: the same text in the same
 * box with transparent glyphs, so the line sits exactly under the typed word. The
 * textarea has no scroll of its own (it grows), so the two never need to be kept in
 * step, and the page scrolls to keep the caret in view.
 *
 * The spans are the server's occurrences, shifted to follow the typing (see
 * `shiftSpans`); this component searches nothing.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import type { Span } from '../../../utils/textEdit'

interface Props {
  value: string
  onChange: (value: string) => void
  spans: Span[]
  label: string
  testId?: string
}

const TEXT: React.CSSProperties = {
  margin: 0, boxSizing: 'border-box', width: '100%', padding: '10px 12px',
  fontFamily: 'var(--font-content)', fontSize: 17, lineHeight: '32px', letterSpacing: 'normal',
  whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'normal',
}

/** *text* cut at the spans' edges: plain runs and underlined ones, in order. */
function segments(text: string, spans: Span[]): { text: string; gap: boolean }[] {
  const out: { text: string; gap: boolean }[] = []
  let at = 0
  for (const [start, end] of [...spans].sort((a, b) => a[0] - b[0])) {
    const s = Math.max(start, at)
    const e = Math.min(end, text.length)
    if (e <= s) continue
    if (s > at) out.push({ text: text.slice(at, s), gap: false })
    out.push({ text: text.slice(s, e), gap: true })
    at = e
  }
  if (at < text.length) out.push({ text: text.slice(at), gap: false })
  return out
}

export default function EditBodyField({ value, onChange, spans, label, testId }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [focused, setFocused] = useState(false)

  // Grow to the text, so the page (not the field) scrolls and the mirror lines up.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  return (
    <div
      style={{
        position: 'relative', borderRadius: 16, background: 'var(--bg-surface)',
        border: `2px solid ${focused ? 'var(--accent)' : 'var(--border)'}`,
      }}
    >
      <div aria-hidden data-testid="edit-body-mirror" style={{ ...TEXT, position: 'absolute', inset: 0, pointerEvents: 'none', color: 'transparent' }}>
        {segments(value, spans).map((seg, i) =>
          seg.gap ? (
            <span
              key={i}
              data-testid="edit-body-gap"
              style={{
                textDecorationLine: 'underline', textDecorationStyle: 'dashed', textDecorationColor: 'var(--accent)',
                textDecorationThickness: 2, textUnderlineOffset: 5,
              }}
            >
              {seg.text}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
        {/* A trailing newline would otherwise have no line to take height. */}
        {value.endsWith('\n') ? '​' : null}
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={label}
        data-testid={testId ?? 'edit-body'}
        rows={4}
        spellCheck={false}
        style={{
          ...TEXT, position: 'relative', display: 'block', resize: 'none', overflow: 'hidden', border: 'none',
          outline: 'none', background: 'transparent', color: 'var(--text-primary)', caretColor: 'var(--accent)',
          minHeight: 160,
        }}
      />
    </div>
  )
}
